import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Edit3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function UserGroupsTab() {
    const { getAuthHeader } = useAuth();
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', member_ids: [] });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [groupsRes, usersRes] = await Promise.all([
                axios.get(`${API}/admin/user-groups`, { headers: getAuthHeader() }),
                axios.get(`${API}/users`, { headers: getAuthHeader() }),
            ]);
            setGroups(groupsRes.data);
            setUsers(usersRes.data);
        } catch { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        if (!form.name.trim()) return toast.error('Please enter a group name');
        try {
            await axios.post(`${API}/admin/user-groups`, form, { headers: getAuthHeader() });
            toast.success('Group created');
            setShowCreateModal(false);
            setForm({ name: '', description: '', member_ids: [] });
            loadData();
        } catch { toast.error('Failed to create group'); }
    };

    const handleUpdate = async () => {
        try {
            await axios.put(`${API}/admin/user-groups/${editingGroup.group_id}`, form, { headers: getAuthHeader() });
            toast.success('Group updated');
            setShowEditModal(false);
            loadData();
        } catch { toast.error('Failed to update group'); }
    };

    const handleDelete = async (groupId) => {
        if (!window.confirm('Delete this group?')) return;
        try {
            await axios.delete(`${API}/admin/user-groups/${groupId}`, { headers: getAuthHeader() });
            toast.success('Group deleted');
            loadData();
        } catch { toast.error('Failed to delete group'); }
    };

    const openEditModal = (group) => {
        setEditingGroup(group);
        setForm({
            name: group.name,
            description: group.description || '',
            member_ids: group.member_ids || [],
        });
        setShowEditModal(true);
    };

    const toggleMember = (userId) => {
        setForm(prev => ({
            ...prev,
            member_ids: prev.member_ids.includes(userId)
                ? prev.member_ids.filter(id => id !== userId)
                : [...prev.member_ids, userId]
        }));
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">User Groups</h2>
                    <p className="text-sm text-stone-500">Organize users into groups to control template access</p>
                </div>
                <Button onClick={() => { setForm({ name: '', description: '', member_ids: [] }); setShowCreateModal(true); }} className="bg-stone-900 hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Group
                </Button>
            </div>

            {groups.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Users className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No user groups yet</h3>
                    <p className="text-stone-500 mb-4">Create groups to organize users and control template access</p>
                    <Button onClick={() => setShowCreateModal(true)}>Create Your First Group</Button>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-4">
                    {groups.map(group => (
                        <div key={group.group_id} className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="font-semibold text-stone-900">{group.name}</h3>
                                    {group.description && <p className="text-sm text-stone-500 mt-0.5">{group.description}</p>}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => openEditModal(group)} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600">
                                        <Edit3 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDelete(group.group_id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-stone-500">
                                <Users className="w-4 h-4" />
                                <span>{group.members?.length || 0} members</span>
                            </div>
                            {group.members && group.members.length > 0 && (
                                <div className="flex -space-x-2 mt-3">
                                    {group.members.slice(0, 5).map(m => (
                                        <div key={m.user_id} className="w-8 h-8 rounded-full bg-stone-100 border-2 border-white flex items-center justify-center text-xs font-semibold text-stone-900" title={m.name}>
                                            {m.name?.charAt(0) || '?'}
                                        </div>
                                    ))}
                                    {group.members.length > 5 && (
                                        <div className="w-8 h-8 rounded-full bg-stone-100 border-2 border-white flex items-center justify-center text-xs font-semibold text-stone-500">
                                            +{group.members.length - 5}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => { if (!open) { setShowCreateModal(false); setShowEditModal(false); } }}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{showEditModal ? 'Edit User Group' : 'Create User Group'}</DialogTitle>
                        <DialogDescription>
                            {showEditModal ? 'Update the group details and members.' : 'Create a new user group for template access control.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label>Group Name *</Label>
                            <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Enterprise Sales Team" className="mt-1" />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of this group" className="mt-1" rows={2} />
                        </div>
                        <div>
                            <Label>Members ({form.member_ids.length} selected)</Label>
                            <div className="mt-2 border border-stone-200 rounded-lg max-h-48 overflow-y-auto">
                                {users.map(user => (
                                    <label key={user.user_id} className="flex items-center gap-3 p-3 hover:bg-stone-50 cursor-pointer border-b border-stone-100 last:border-b-0">
                                        <input type="checkbox" checked={form.member_ids.includes(user.user_id)} onChange={() => toggleMember(user.user_id)} className="rounded border-stone-300 text-stone-700" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-stone-800">{user.name}</p>
                                            <p className="text-xs text-stone-500">{user.email}</p>
                                        </div>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 capitalize">{user.role?.replace('_', ' ')}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>Cancel</Button>
                        <Button onClick={showEditModal ? handleUpdate : handleCreate} className="bg-stone-900 hover:bg-stone-800">
                            {showEditModal ? 'Save Changes' : 'Create Group'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function AdminUserGroupsPage() {
    return (
        <AdminLayout title="User Groups" description="Organize users into groups for template access control">
            <UserGroupsTab />
        </AdminLayout>
    );
}
